import React, { useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

export const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, signIn, confirmSignIn } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return; // Hard guard against rapid double-submit
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      if (step === 'email') {
        await signIn(email);
        setStep('code');
      } else {
        await confirmSignIn(code);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Lock sx={{ fontSize: 48 }} />
          </Box>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
            Sign In
          </Typography>
          <form onSubmit={handleSubmit}>
            {step === 'email' ? (
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
              />
            ) : (
              <TextField
                fullWidth
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                sx={{ mb: 3 }}
              />
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting || (step === 'email' ? !email : !code)}
            >
              {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : step === 'email' ? 'Send Code' : 'Confirm'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
